/**
 * DemoPage — public, no-auth, no-shell signing playground.
 *
 * Five tabs, one per pitch persona. Each tab pre-fills three fields,
 * canonicalises them as JSON, hashes (SHA-256), and Ed25519-signs the
 * hash with a hard-coded DEMO keypair (clearly labelled). The result —
 * payload, hash, signature, and a QR pointing at `/v/{hash}` — lands
 * on screen in well under three seconds.
 *
 * Crypto runs entirely in the browser. No server is called from this
 * page; the QR target is a real verifier URL (`/v/:id`) but the demo
 * id won't be known to a fresh engine — that's expected for a stunt
 * surface meant to live on its own.
 *
 * Path: `/demo` (registered in App.tsx as a public route, no shell).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ed25519 } from '@noble/curves/ed25519';
import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex } from '@noble/hashes/utils';
import QRCode from 'qrcode';

// ─── DEMO KEYPAIR ────────────────────────────────────────────────
//
// Hardcoded, clearly marked. This key has NO production meaning —
// it lives in client code where anyone can read it. Used only so the
// demo page can show a real Ed25519 signature without first running
// a full key-derivation flow. Production signing flows use
// per-browser operator keys generated in `src/api/http.ts`.
//
// Generated once with `ed25519.utils.randomPrivateKey()` and pasted.
const DEMO_SECRET_HEX =
  '7c9d2e3f4a5b6c7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d';
const DEMO_PUBLIC_HEX = bytesToHex(
  ed25519.getPublicKey(hexToBytes(DEMO_SECRET_HEX)),
);

// ─── Tabs ────────────────────────────────────────────────────────
interface DemoField {
  /** UI label, shown above the input */
  label: string;
  /** Pre-filled value */
  value: string;
}

interface DemoTab {
  key: string;
  emoji: string;
  /** Hebrew + English headline shown in the tab pill */
  label: string;
  /** Subtitle directly under the page header for that tab */
  subtitle: string;
  /** Three pre-filled fields */
  fields: DemoField[];
}

const TABS: readonly DemoTab[] = [
  {
    key: 'citizen-mirror',
    emoji: '🏛️',
    label: 'Citizen Mirror',
    subtitle: 'Local council · Decision signed at the moment of the vote',
    fields: [
      { label: 'Decision', value: 'Decision #47: Road-maintenance budget approval ₪2.3M' },
      { label: 'Council', value: 'Pardes Hanna-Karkur + Emek Hefer' },
      { label: 'Vote tally', value: 'For: 12  ·  Against: 1  ·  Abstain: 2' },
    ],
  },
  {
    key: 'gate-to-shelf',
    emoji: '🏭',
    label: 'Gate to Shelf',
    subtitle: 'Industry · A batch signed from factory gate to shelf',
    fields: [
      { label: 'Batch', value: 'Batch #PG-2026-0847 Prigat Orange 1.5L' },
      { label: 'Cold-chain', value: 'Temp: 4.2°C  ·  Humidity: 62%  ·  PASS' },
      { label: 'Customer', value: 'Sano-Bruno Distribution  ·  Shufersal DC' },
    ],
  },
  {
    key: 'amar-reporter',
    emoji: '🏥',
    label: 'AMAR Reporter',
    subtitle: 'Medical devices · Medical-device report signed within 48 hours',
    fields: [
      { label: 'Incident', value: 'Incident #MR-119: Device malfunction X-Ray Pro' },
      { label: 'Severity', value: 'Severity: Medium  ·  Patient impact: None' },
      { label: 'Reporter', value: 'Promedico  ·  Hospital: Beilinson  ·  Dept: Imaging' },
    ],
  },
  {
    key: 'research-shield',
    emoji: '🎓',
    label: 'Research Shield',
    subtitle: 'Academia · Research submission signed at the moment of submission',
    fields: [
      { label: 'Submission', value: 'ML-Based Water Quality Analysis' },
      { label: 'Author', value: 'Dr. Sarah Cohen  ·  Ruppin Academic Center' },
      { label: 'Co-authors', value: 'Prof. Y. Levi, M. Avraham (PhD candidate)' },
    ],
  },
  {
    key: 'cyber-shield',
    emoji: '🔒',
    label: 'Cyber Shield',
    subtitle: 'Cyber protection · Events signed in real time',
    fields: [
      { label: 'Alert', value: 'Alert #CS-031: Unauthorized access attempt' },
      { label: 'Source', value: 'Source IP: 185.x.x.x  ·  Geo: TR/IR' },
      { label: 'Action', value: 'Blocked at WAF  ·  SOC notified  ·  Ticket open' },
    ],
  },
] as const;

// ─── Helpers ─────────────────────────────────────────────────────
function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function base64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

/**
 * Canonical JSON — sorted keys, stable spacing. Same idea as RFC 8785
 * (JCS) but small enough to live inline. Sufficient for a demo where
 * payload shape is fixed at three string fields.
 */
function canonical(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  const keys = Object.keys(value as Record<string, unknown>).sort();
  const body = keys
    .map((k) => `${JSON.stringify(k)}:${canonical((value as Record<string, unknown>)[k])}`)
    .join(',');
  return `{${body}}`;
}

interface ProofBundle {
  payload: Record<string, string>;
  payload_canonical: string;
  sha256_hex: string;
  signature_b64: string;
  pubkey_hex: string;
  signed_at_iso: string;
  algo: 'ed25519';
  /** Demo-mode marker so anyone reading the JSON knows the key is public */
  demo: true;
}

function sign(payload: Record<string, string>): ProofBundle {
  const canonicalStr = canonical(payload);
  const bytes = new TextEncoder().encode(canonicalStr);
  const hashBytes = sha256(bytes);
  const sig = ed25519.sign(hashBytes, hexToBytes(DEMO_SECRET_HEX));
  return {
    payload,
    payload_canonical: canonicalStr,
    sha256_hex: bytesToHex(hashBytes),
    signature_b64: base64(sig),
    pubkey_hex: DEMO_PUBLIC_HEX,
    signed_at_iso: new Date().toISOString(),
    algo: 'ed25519',
    demo: true,
  };
}

function verify(bundle: ProofBundle): boolean {
  const bytes = new TextEncoder().encode(bundle.payload_canonical);
  const hashBytes = sha256(bytes);
  if (bytesToHex(hashBytes) !== bundle.sha256_hex) return false;
  const sigBytes = Uint8Array.from(atob(bundle.signature_b64), (c) => c.charCodeAt(0));
  return ed25519.verify(sigBytes, hashBytes, hexToBytes(bundle.pubkey_hex));
}

// ─── Component ───────────────────────────────────────────────────
export function DemoPage() {
  // Deep-link via `?persona=<slug>` from MarketplaceDetailPage's
  // Overview tab. Falls back to the first tab when missing or
  // unknown.
  const initialKey = (() => {
    if (typeof window === 'undefined') return TABS[0]!.key;
    const requested = new URLSearchParams(window.location.search).get('persona');
    if (requested && TABS.some((t) => t.key === requested)) return requested;
    return TABS[0]!.key;
  })();
  const [activeKey, setActiveKey] = useState<string>(initialKey);
  const active = useMemo(
    () => TABS.find((t) => t.key === activeKey) ?? TABS[0]!,
    [activeKey],
  );

  // Per-tab editable values, keyed by tab.key. Initialised once from
  // the static defaults above so that switching tabs doesn't lose
  // edits the user made on a previous tab.
  const [values, setValues] = useState<Record<string, string[]>>(() => {
    const seed: Record<string, string[]> = {};
    for (const t of TABS) seed[t.key] = t.fields.map((f) => f.value);
    return seed;
  });

  const [bundle, setBundle] = useState<ProofBundle | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [signedAt, setSignedAt] = useState<number | null>(null); // ms elapsed
  const [verifyOk, setVerifyOk] = useState<boolean | null>(null);
  const [signing, setSigning] = useState(false);
  const verifyTriggered = useRef(false);

  // Reset the proof when the user switches tabs — the previous tab's
  // signature shouldn't linger under a different headline.
  useEffect(() => {
    setBundle(null);
    setQrDataUrl(null);
    setSignedAt(null);
    setVerifyOk(null);
    verifyTriggered.current = false;
  }, [activeKey]);

  const onSign = async () => {
    setSigning(true);
    setVerifyOk(null);
    setBundle(null);
    setQrDataUrl(null);
    const t0 = performance.now();
    // One animation frame so the "signing…" state is visible. The
    // actual crypto is faster than the screen refreshes.
    await new Promise((r) => requestAnimationFrame(() => r(undefined)));

    const payload: Record<string, string> = {};
    active.fields.forEach((f, i) => {
      payload[f.label] = values[active.key]?.[i] ?? f.value;
    });
    const b = sign(payload);

    const verifierUrl = `${window.location.origin}/v/${b.sha256_hex}`;
    const qr = await QRCode.toDataURL(verifierUrl, {
      width: 320,
      margin: 1,
      color: { dark: '#1A1A2E', light: '#FFFFFF' },
      errorCorrectionLevel: 'M',
    });

    const elapsed = performance.now() - t0;
    setBundle(b);
    setQrDataUrl(qr);
    setSignedAt(elapsed);
    setSigning(false);

    // Re-verify on the next tick so the green check appears AFTER the
    // result is rendered — feels deliberate rather than instant.
    if (!verifyTriggered.current) {
      verifyTriggered.current = true;
      window.setTimeout(() => setVerifyOk(verify(b)), 250);
    }
  };

  const onDownload = () => {
    if (!bundle) return;
    const blob = new Blob([JSON.stringify(bundle, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${active.key}-proof-${bundle.sha256_hex.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const updateField = (idx: number, value: string) => {
    setValues((prev) => {
      const next = { ...prev };
      const arr = (next[active.key] ?? []).slice();
      arr[idx] = value;
      next[active.key] = arr;
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-canvas text-ink">
      {/* Header */}
      <header className="border-b border-border bg-surface">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-spark text-base font-bold text-white">
              GI
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight">Growing Intelligence</div>
              <div className="text-xs text-ink-muted">Live signing demo</div>
            </div>
          </div>
          <a
            href="/"
            className="text-sm text-ink-muted hover:text-ink"
          >
            growing-intelligence.com →
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        {/* Tab strip */}
        <div role="tablist" aria-label="Demo personas" className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const isActive = t.key === activeKey;
            return (
              <button
                key={t.key}
                role="tab"
                type="button"
                aria-selected={isActive}
                onClick={() => setActiveKey(t.key)}
                className={[
                  'inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-spark bg-spark text-white shadow-sm'
                    : 'border-border bg-surface text-ink-muted hover:border-spark hover:text-ink',
                ].join(' ')}
              >
                <span aria-hidden>{t.emoji}</span>
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* Active tab body */}
        <section className="mt-8 grid gap-6 lg:grid-cols-2">
          {/* Form */}
          <div className="rounded-xl border border-border bg-surface p-6 shadow-subtle">
            <h2 className="text-xl font-semibold tracking-tight">
              {active.emoji} {active.label}
            </h2>
            <p className="mt-1 text-sm text-ink-muted" dir="ltr">
              {active.subtitle}
            </p>

            <div className="mt-6 space-y-4">
              {active.fields.map((f, i) => (
                <div key={f.label}>
                  <label
                    htmlFor={`demo-${active.key}-${i}`}
                    className="block text-xs font-semibold uppercase tracking-wider text-ink-muted"
                  >
                    {f.label}
                  </label>
                  <input
                    id={`demo-${active.key}-${i}`}
                    type="text"
                    value={values[active.key]?.[i] ?? f.value}
                    onChange={(e) => updateField(i, e.target.value)}
                    className="mt-1.5 w-full rounded-lg border border-border bg-canvas px-3 py-2 text-sm text-ink focus:border-spark focus:outline-none focus:ring-2 focus:ring-spark/20"
                  />
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={onSign}
              disabled={signing}
              className="mt-6 inline-flex w-full items-center justify-center rounded-lg bg-spark px-4 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-spark/90 disabled:opacity-60"
            >
              {signing ? 'Signing…' : 'Sign & Prove'}
            </button>

            <p className="mt-3 text-center text-[11px] text-ink-subtle">
              Ed25519 · client-side · 0 server calls · DEMO key (public on purpose)
            </p>
          </div>

          {/* Result */}
          <div className="rounded-xl border border-border bg-surface p-6 shadow-subtle">
            {!bundle ? (
              <EmptyResult />
            ) : (
              <Result
                bundle={bundle}
                qrDataUrl={qrDataUrl}
                elapsedMs={signedAt}
                verifyOk={verifyOk}
                onDownload={onDownload}
              />
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────
function EmptyResult() {
  return (
    <div className="grid h-full place-items-center text-center text-ink-muted">
      <div>
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-spark-soft text-spark">
          <svg viewBox="0 0 20 20" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.7">
            <path d="M4 11l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <p className="mt-3 text-sm">Click <span className="font-semibold text-ink">Sign &amp; Prove</span> to generate a real Ed25519 proof.</p>
      </div>
    </div>
  );
}

interface ResultProps {
  bundle: ProofBundle;
  qrDataUrl: string | null;
  elapsedMs: number | null;
  verifyOk: boolean | null;
  onDownload: () => void;
}

function Result({ bundle, qrDataUrl, elapsedMs, verifyOk, onDownload }: ResultProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Signed proof</h3>
        {elapsedMs !== null && (
          <span className="text-xs text-ink-muted">
            Signed in {Math.max(1, Math.round(elapsedMs))} ms
          </span>
        )}
      </div>

      {qrDataUrl && (
        <div className="flex items-center gap-4 rounded-lg border border-border bg-canvas p-4">
          <img
            src={qrDataUrl}
            alt="Verifier QR"
            width={140}
            height={140}
            className="h-[140px] w-[140px] flex-shrink-0 rounded"
          />
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
              Verifier
            </div>
            <div className="mt-1 break-all font-mono text-xs text-ink">
              /v/{bundle.sha256_hex.slice(0, 16)}…
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onDownload}
                className="inline-flex items-center rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:border-spark hover:text-spark"
              >
                Download proof.json
              </button>
              {verifyOk !== null && (
                <span
                  className={[
                    'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold',
                    verifyOk
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-rose-50 text-rose-700',
                  ].join(' ')}
                >
                  <span aria-hidden>{verifyOk ? '✓' : '✗'}</span>
                  {verifyOk ? 'Locally verified' : 'Verify failed'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <Field label="SHA-256" value={bundle.sha256_hex} />
      <Field label="Signature" value={bundle.signature_b64} />
      <Field label="Public key" value={bundle.pubkey_hex} />
      <Field label="Signed at" value={bundle.signed_at_iso} />

      <details className="rounded-lg border border-border bg-canvas p-3 text-xs">
        <summary className="cursor-pointer font-semibold text-ink-muted">
          Canonical payload
        </summary>
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all font-mono text-[11px] text-ink">
          {bundle.payload_canonical}
        </pre>
      </details>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        {label}
      </div>
      <div className="mt-1 break-all font-mono text-xs text-ink">{value}</div>
    </div>
  );
}
